import React, { useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import ReactPlayer from 'react-player';
import { MB_customWindowForWeb } from '@/utils/general/custom-window-for-web';
import { MB_VideoPlayerProps, MB_VideoPlayerPlayStatus, PreviewComponent } from './MB_VideoPlayerUtils';
import { StyleSheet, Text, View } from 'react-native';
import { COLORS } from './colors';
import screenfull from 'screenfull';
import { MB_SVG_BrokenFile } from '@/components/general/svg-components';
import { MB_Pressable, MB_PressableStateCallbackType } from '@/components/general/pressable';

const MB_VideoPlayer = React.memo(
  ({
    MB_Ref,
    sourceUrl,
    showControls,
    isLooping,
    isMuted,
    shouldPlay,
    onFullscreenUpdate,
    onError,
    failed,
    failedMessage,
    containerStyle,
    showPreview,
    previewImageUrl,
    previewContainerStyle,
    onPreviewPressed,
    onReady,
    onPlayStatusChanged,
    isFocused,
    loadingComponent,
    onProgressUpdate,
  }: MB_VideoPlayerProps) => {
    const [playing, setPlaying] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [showControlsInternal, setShowControlsInternal] = useState<boolean | undefined>();
    const videoPlayerRef = useRef<ReactPlayer>(null);

    // react-player 3 hands us the media element itself as the ref (v2 exposed a
    // wrapper with seekTo/getInternalPlayer). These helpers work with either.
    const getMediaElement = useCallback((hlsKey?: 'hls'): any => {
      const ref = videoPlayerRef.current as any;
      if (!ref) {
        return undefined;
      }
      if (typeof ref.getInternalPlayer === 'function') {
        return ref.getInternalPlayer(hlsKey);
      }
      return hlsKey ? ref.api?.hls ?? ref.hls : ref;
    }, []);

    const seekToSeconds = useCallback((seconds: number) => {
      const ref = videoPlayerRef.current as any;
      if (!ref) {
        return;
      }
      if (typeof ref.seekTo === 'function') {
        ref.seekTo(seconds);
      } else if ('currentTime' in ref) {
        ref.currentTime = seconds;
      }
    }, []);

    const stopVideo = useCallback(() => {
      if (playing) {
        setPlaying(false);
      }
      seekToSeconds(0);
    }, [playing, seekToSeconds]);

    useImperativeHandle(MB_Ref, () => ({
      play: position => {
        if (playing) {
          if (position) {
            seekToSeconds(position);
          }
        }

        if (!playing) {
          setPlaying(true);
        }
      },
      pause: () => {
        if (playing) {
          setPlaying(false);
        }
      },
      stop: () => {
        stopVideo();
      },
      presentFullscreenPlayer: () => {
        // We needed the as any part here to prevent typescript error
        screenfull.request(getMediaElement() as any);
      },
      isInvisibleVideo: () => {
        let isInvisible = false;
        if (!getMediaElement('hls')) {
          const media = getMediaElement();
          isInvisible = media?.videoHeight === 0 || media?.videoWidth === 0;
        }

        return isInvisible;
      },
    }));

    // Mirror the native player: a `shouldPlay` change after mount pauses/resumes.
    // (Previously only the initial value was honoured, in onReady.)
    useEffect(() => {
      if (shouldPlay !== undefined) {
        setPlaying(shouldPlay);
      }
    }, [shouldPlay]);

    // Note: Need this because video player flashes the native controls initially if it was directly set to true.
    useEffect(() => {
      let nativeControlsSetTimeout = setTimeout(() => setShowControlsInternal(showControls), 1000);

      return () => {
        clearTimeout(nativeControlsSetTimeout);
      };
    }, [showControls]);

    const isNativeControlsEnabledByTempFixed = (isHovered: boolean) => {
      if (isHovered && showControls && showControlsInternal === undefined) {
        return true;
      }

      return false;
    };

    if (failed) {
      return (
        <View style={[{ alignItems: 'center', justifyContent: 'center' }, containerStyle]}>
          <MB_SVG_BrokenFile color={COLORS.white} size={64} />
          <Text style={styles.failedText}>{failedMessage}</Text>
        </View>
      );
    }

    // For cases where we navigate to other page while the page that is playing
    // the video stays mounted. We will basically detect that view is not in focus
    // and will unmount the video
    if (isFocused === false) {
      return null;
    }

    return (
      <MB_Pressable style={containerStyle}>
        {({ hovered }: MB_PressableStateCallbackType) => {
          return (
            <>
              {loadingComponent && isLoading && <View style={styles.loadingContainer}>{loadingComponent}</View>}
              <ReactPlayer
                width={'100%'}
                height={'100%'}
                ref={videoPlayerRef}
                // react-player 3 reads `src`; the pre-v3 `url` prop is silently ignored
                // (the player never received a source after the v3 upgrade).
                src={typeof sourceUrl === 'string' ? sourceUrl : undefined}
                playing={playing}
                // Signing speed is the content: hide the browser's playback-speed menu
                // and snap the rate back to 1.0 if anything (devtools, extensions,
                // keyboard shortcuts) still changes it.
                playbackRate={1}
                controlsList="noplaybackrate"
                onRateChange={(event: React.SyntheticEvent<HTMLVideoElement>) => {
                  const player = event.currentTarget as unknown as { playbackRate: number } | null;
                  if (player && player.playbackRate !== 1) {
                    player.playbackRate = 1;
                  }
                }}
                onStart={() => {
                  setPlaying(true);
                  onPlayStatusChanged?.(MB_VideoPlayerPlayStatus.started);
                }}
                onPlay={() => {
                  setPlaying(true);
                  onPlayStatusChanged?.(MB_VideoPlayerPlayStatus.playing);
                }}
                onPause={() => {
                  setPlaying(false);
                  onPlayStatusChanged?.(MB_VideoPlayerPlayStatus.paused);
                }}
                onEnded={() => {
                  setPlaying(false);
                  onPlayStatusChanged?.(MB_VideoPlayerPlayStatus.ended);
                }}
                onTimeUpdate={(event: React.SyntheticEvent<HTMLVideoElement>) => {
                  if (!onProgressUpdate) {
                    return;
                  }
                  const media = event.currentTarget as unknown as { currentTime: number; duration: number } | null;
                  if (media) {
                    onProgressUpdate(media.currentTime * 1000, Number.isFinite(media.duration) ? media.duration * 1000 : 0);
                  }
                }}
                onError={(error: any, data: any, hlsInstance: any, hlsGlobal: any) => {
                  console.log('Video player error', { error, data, hlsInstance, hlsGlobal });
                  setIsLoading(false);
                  onError?.(error);
                }}
                light={showPreview}
                playIcon={<PreviewComponent previewImageUrl={previewImageUrl} previewContainerStyle={previewContainerStyle} />}
                onClickPreview={() => {
                  onPreviewPressed?.();
                  setPlaying(true);
                }}
                onReady={() => {
                  // TODO: Maybe add disableInitialQualityCheck to control this ?
                  const hls = getMediaElement('hls');
                  if (hls?.config) {
                    // We end up downloading a low quality chunk at first to test the bandwidth
                    // results in start level using higher quality chunks if bandwidth allows it.
                    hls.config.testBandwidth = true;
                    hls.config.startLevel = -1;
                  }
                  setIsLoading(false);
                  onReady?.();
                  if (shouldPlay && shouldPlay !== playing) {
                    setPlaying(shouldPlay);
                  }

                  const media = getMediaElement();
                  media?.addEventListener?.('fullscreenchange', (event: any) => {
                    const isFullScreen =
                      MB_customWindowForWeb.document.fullscreenElement === event.target || MB_customWindowForWeb.document.webkitFullscreenElement === event.target;
                    onFullscreenUpdate?.(isFullScreen);
                  });
                  media?.addEventListener?.('webkitfullscreenchange', (event: any) => {
                    const isFullScreen =
                      MB_customWindowForWeb.document.fullscreenElement === event.target || MB_customWindowForWeb.document.webkitFullscreenElement === event.target;
                    onFullscreenUpdate?.(isFullScreen);
                  });
                  media?.addEventListener?.('webkitbeginfullscreen', () => {
                    const isFullScreen = true;
                    onFullscreenUpdate?.(isFullScreen);
                  });
                  media?.addEventListener?.('webkitendfullscreen', () => {
                    const isFullScreen = false;
                    onFullscreenUpdate?.(isFullScreen);
                  });
                  media?.addEventListener?.('MSFullscreenChange', (event: any) => {
                    const isFullScreen =
                      MB_customWindowForWeb.document.fullscreenElement === event.target ||
                      MB_customWindowForWeb.document.webkitFullscreenElement === event.target ||
                      MB_customWindowForWeb.document.msFullscreenElement === event.target;
                    onFullscreenUpdate?.(isFullScreen);
                  });
                }}
                controls={showControlsInternal ?? isNativeControlsEnabledByTempFixed(hovered ?? false)}
                loop={isLooping}
                muted={isMuted}
              />
            </>
          );
        }}
      </MB_Pressable>
    );
  }
);

export { MB_VideoPlayer };

const styles = StyleSheet.create({
  failedText: {
    fontSize: 16,
    fontWeight: '500',
    color: COLORS.white,
    textAlign: 'center',
    marginTop: 16,
  },
  loadingContainer: {
    position: 'absolute',
    width: '100%',
    height: '100%',
    justifyContent: 'center',
    alignItems: 'center',
  },
});
